import React from 'react';
import { X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import {
  ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent,
} from '@/components/ui/chart';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  KpiCard, ChartCard, ExportMenu, ScreenHeader, EmptyMeter, AXIS_TICK, CHART_FILL,
} from '@/components/app/kit.jsx';
import { useOps } from '@/hooks/useOps.jsx';
import { resolveDoctorName } from '@/lib/ops-data.js';
import { totalINR, groupSum, ranked, dailyStacked, dailyTotals } from '@/lib/aggregate.js';
import { fmtINR, fmtCompact } from '@/lib/format.js';
import { downloadCsv, downloadTxtReport } from '@/lib/export.js';
import { LoadingBody, ErrorBody } from './Overview.jsx';

// Attribution — THE owner's key screen. Group-by switcher (Hospital | Department |
// Doctor | Use case) drives a stacked daily bar chart + a ranked table (spend,
// share %, trend sparkline). Clicking an entity drills the WHOLE screen down to it
// (e.g. one doctor → their per-usecase split). The "platform / unattributed"
// bucket ('-' dims) is always shown honestly, never hidden or redistributed.

const DIMS = [
  { key: 'hospital', label: 'Hospital' },
  { key: 'department', label: 'Department' },
  { key: 'doctor', label: 'Doctor' },
  { key: 'usecase', label: 'Use case' },
];
const UNATTRIBUTED = 'platform / unattributed';

function dimValue(row, dim) {
  const v = row[dim];
  if (!v || v === '-') return UNATTRIBUTED;
  return v;
}
function displayName(dim, key) {
  if (key === UNATTRIBUTED) return UNATTRIBUTED;
  if (dim === 'doctor') return resolveDoctorName(key);
  return key;
}

// Tiny inline sparkline (app idiom, mirrors opd-app's hand-drawn Sparkline).
function Sparkline({ data, width = 84, height = 24, color = 'hsl(var(--chart-1))' }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1e-9);
  const min = Math.min(...data, 0);
  const span = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * (width - 2) + 1;
    const y = height - 2 - ((v - min) / span) * (height - 4);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={width} height={height} className="inline-block align-middle" aria-hidden>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export default function Attribution({ range }) {
  const ops = useOps();
  const [dim, setDim] = React.useState('hospital');
  const [drill, setDrill] = React.useState(null); // { dim, key } — screen-wide filter

  if (ops.loading) return <LoadingBody />;
  if (ops.error) return <ErrorBody error={ops.error} />;
  const all = ops.enriched;
  if (!all.length) return <div className="p-6"><ScreenHeader title="Attribution" /><EmptyMeter /></div>;

  // Drill-down filters the whole screen to the chosen entity; the group-by then
  // shows that entity's split along the current dimension.
  const items = drill
    ? all.filter((r) => dimValue(r, drill.dim) === drill.key)
    : all;

  // When drilled into an entity on dim X, default the visible grouping to a
  // DIFFERENT dimension (a doctor's per-usecase split is the canonical ask).
  const activeDim = drill && drill.dim === dim
    ? (DIMS.find((d) => d.key !== dim)?.key || dim)
    : dim;

  const grand = totalINR(items);
  const rankedRows = ranked(groupSum(items, (r) => dimValue(r, activeDim)));

  // Stacked daily bars for the top entities (+ Other so the total is honest).
  const topKeys = rankedRows.slice(0, 5).map((r) => r.key);
  const hasOther = rankedRows.length > topKeys.length;
  const keyFn = (r) => {
    const k = dimValue(r, activeDim);
    return topKeys.includes(k) ? k : 'Other';
  };
  const seriesKeys = hasOther ? [...topKeys, 'Other'] : topKeys;
  const stacked = dailyStacked(items, keyFn, seriesKeys, range);
  const chartConfig = Object.fromEntries(seriesKeys.map((k, i) => [
    k, { label: displayName(activeDim, k), color: k === 'Other' ? 'hsl(var(--muted-foreground))' : CHART_FILL(i) },
  ]));

  // Per-entity daily series for the table sparklines.
  const sparkByKey = new Map();
  for (const r of rankedRows.slice(0, 12)) {
    const sub = items.filter((it) => dimValue(it, activeDim) === r.key);
    sparkByKey.set(r.key, dailyTotals(sub, range).map((d) => d.value));
  }

  const unattributedRow = rankedRows.find((r) => r.key === UNATTRIBUTED);
  const dimLabel = (DIMS.find((d) => d.key === activeDim) || {}).label || activeDim;

  const csv = () => downloadCsv(
    `ops-attribution-${activeDim}${drill ? `-${drill.key}` : ''}-${range.from}_${range.to}.csv`,
    [
      { key: 'entity', label: dimLabel },
      { key: 'inr', label: 'Spend (INR)' },
      { key: 'share', label: 'Share %' },
    ],
    rankedRows.map((r) => ({ entity: displayName(activeDim, r.key), inr: r.inr.toFixed(2), share: r.share.toFixed(1) })),
  );
  const txt = () => downloadTxtReport(
    `ops-attribution-${activeDim}${drill ? `-${drill.key}` : ''}-${range.from}_${range.to}.txt`, {
      title: 'Healther Ops — Attribution',
      meta: [
        `Range: ${range.from} → ${range.to}`,
        `Grouped by: ${dimLabel}`,
        drill ? `Drilled into: ${displayName(drill.dim, drill.key)} (${drill.dim})` : 'Scope: everything',
        `Total (INR): ${fmtINR(grand.inr)}`,
      ],
      sections: [{
        heading: `Spend by ${dimLabel.toLowerCase()}`,
        columns: [{ key: 'entity', label: dimLabel }, { key: 'inr', label: 'INR' }, { key: 'share', label: 'Share' }],
        rows: rankedRows.map((r) => ({ entity: displayName(activeDim, r.key), inr: Math.round(r.inr), share: r.share.toFixed(1) + '%' })),
      }],
    },
  );

  return (
    <div className="p-6 cc-enter">
      <ScreenHeader
        title="Attribution"
        subtitle="Who the money goes to — hospital, department, doctor, use case"
      >
        <Tabs value={activeDim} onValueChange={(v) => { setDim(v); }}>
          <TabsList>
            {DIMS.map((d) => <TabsTrigger key={d.key} value={d.key}>{d.label}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <ExportMenu onCsv={csv} onTxt={txt} />
      </ScreenHeader>

      {drill && (
        <div className="mb-4 flex items-center gap-2">
          <Badge variant="secondary" className="h-7 gap-1.5 px-2.5 text-[12px]">
            {(DIMS.find((d) => d.key === drill.dim) || {}).label}: {displayName(drill.dim, drill.key)}
          </Badge>
          <Button variant="outline" size="sm" onClick={() => setDrill(null)}>
            <X className="size-3.5" /> Clear drill-down
          </Button>
          <span className="text-[12px] text-muted-foreground">
            Showing only this entity's spend, split by {dimLabel.toLowerCase()}.
          </span>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-3">
        <KpiCard label={drill ? 'Entity spend' : 'Total spend'} value={fmtINR(grand.inr)} sub="range total, INR" />
        <KpiCard label={`${dimLabel}s`} value={rankedRows.length}
          sub={`top: ${rankedRows[0] ? displayName(activeDim, rankedRows[0].key) : '—'}`} />
        <KpiCard label="Top share" value={rankedRows[0] ? `${rankedRows[0].share.toFixed(0)}%` : '—'}
          sub={rankedRows[0] ? fmtINR(rankedRows[0].inr) : ''} />
        <KpiCard label="Unattributed" value={unattributedRow ? `${unattributedRow.share.toFixed(0)}%` : '0%'}
          sub={unattributedRow ? `${fmtINR(unattributedRow.inr)} platform-level` : 'everything attributed'} />
      </div>

      <ChartCard
        title={`Daily spend by ${dimLabel.toLowerCase()}`}
        subtitle={`Stacked INR/day · top ${topKeys.length}${hasOther ? ' + other' : ''}${drill ? ` · within ${displayName(drill.dim, drill.key)}` : ''}`}
        empty={stacked.length === 0}
      >
        <ChartContainer config={chartConfig} className="h-[230px] w-full">
          <BarChart data={stacked} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} tick={AXIS_TICK} minTickGap={24} />
            <YAxis width={52} tickLine={false} axisLine={false} tick={AXIS_TICK} tickFormatter={(v) => fmtCompact(v)} />
            <ChartTooltip content={<ChartTooltipContent formatter={(v) => fmtINR(v)} />} />
            <ChartLegend content={<ChartLegendContent />} />
            {seriesKeys.map((k, i) => (
              <Bar key={k} dataKey={k} stackId="attr"
                fill={k === 'Other' ? 'hsl(var(--muted-foreground))' : CHART_FILL(i)}
                radius={i === seriesKeys.length - 1 ? [3, 3, 0, 0] : 0} />
            ))}
          </BarChart>
        </ChartContainer>
      </ChartCard>

      <Card className="mt-4 gap-0 overflow-hidden py-0">
        <CardHeader className="border-b py-4">
          <CardTitle className="text-[13.5px]">Ranked by spend</CardTitle>
          <CardDescription className="text-[11.5px]">
            Click a row to drill the whole screen down to that {dimLabel.toLowerCase()}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-4">{dimLabel}</TableHead>
                  <TableHead className="text-right">Spend (INR)</TableHead>
                  <TableHead className="text-right">Share</TableHead>
                  <TableHead className="pr-4 text-right">Trend</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rankedRows.map((r, i) => (
                  <TableRow
                    key={r.key}
                    className="cursor-pointer"
                    onClick={() => {
                      setDrill({ dim: activeDim, key: r.key });
                      // Grouping flips to a different dim so the drill-down shows a split.
                      const next = DIMS.find((d) => d.key !== activeDim);
                      if (next) setDim(next.key);
                    }}
                  >
                    <TableCell className="pl-4 font-medium text-foreground">
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block size-2 rounded-[2px]"
                          style={{ background: r.key === UNATTRIBUTED ? 'hsl(var(--muted-foreground))' : CHART_FILL(i) }} />
                        {displayName(activeDim, r.key)}
                        {r.key === UNATTRIBUTED && (
                          <Badge variant="outline" className="h-4 px-1.5 text-[9.5px] text-muted-foreground">shared keys / infra</Badge>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium text-foreground">{fmtINR(r.inr)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{r.share.toFixed(1)}%</TableCell>
                    <TableCell className="pr-4 text-right">
                      <Sparkline data={sparkByKey.get(r.key)} color={r.key === UNATTRIBUTED ? 'hsl(var(--muted-foreground))' : CHART_FILL(i)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
